import React from 'react';
import { AlertTriangle, XCircle, CheckCircle2, Zap, TrendingUp, FileText, ShieldCheck, ArrowRight } from 'lucide-react';
import { StatCard } from '../../../../shared/StatCard';
import { Badge } from '../../../../../components/ui/Badge';
import { Card } from '../../../../../components/ui/Card';
import { MODULE_CONFIG, PIPELINE_STAGES, SEVERITY_CONFIG } from '../shared';
import type { GovernanceStats, ModuleHealth, UnifiedQuestionRow } from '../hooks/useGovernanceData';
import type { IssueDomain } from '../../../../../governance';
interface OverviewDashboardProps {
  stats: GovernanceStats;
  moduleHealth: ModuleHealth[];
  topBlockers: UnifiedQuestionRow[];
  pipelineIssueCounts: Record<IssueDomain, number> | null;
  isRunning: boolean;
  onNavigateToQuestion: (filter?: string) => void;
}
export function OverviewDashboard({
  stats,
  moduleHealth,
  topBlockers,
  pipelineIssueCounts,
  isRunning,
  onNavigateToQuestion
}: OverviewDashboardProps) {
  const isHealthy = stats.fail === 0 && stats.review === 0;
  return <div className="space-y-6" data-id="element-2575">
      {/* Health Banner */}
      <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${isHealthy ? 'bg-emerald-50 border-emerald-200' : stats.fail > 0 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`} data-id="element-2576">
        {isHealthy ? <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0" data-id="element-2577" /> : stats.fail > 0 ? <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" data-id="element-2578" /> : <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" data-id="element-2579" />}
        <div className="flex-1 min-w-0" data-id="element-2580">
          <span className={`text-sm font-semibold ${isHealthy ? 'text-emerald-800' : stats.fail > 0 ? 'text-red-800' : 'text-amber-800'}`} data-id="element-2581">
            {isHealthy ? 'Systeem gezond' : stats.fail > 0 ? 'Kritieke fouten gevonden' : 'Aandachtspunten gevonden'}
          </span>
          {!isHealthy && <span className={`text-xs ml-2 ${stats.fail > 0 ? 'text-red-700' : 'text-amber-700'}`} data-id="element-2582">
              {[stats.blockerCount > 0 && `${stats.blockerCount} blockers`, stats.warningCount > 0 && `${stats.warningCount} warnings`].filter(Boolean).join(' · ')}
            </span>}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2583">
        <StatCard label="Totaal Vragen" value={stats.total.toLocaleString('nl-NL')} icon={<FileText className="w-5 h-5" data-id="element-2585" />} color="blue" data-id="element-2584" />
        <StatCard label="Geslaagd" value={stats.pass.toLocaleString('nl-NL')} icon={<CheckCircle2 className="w-5 h-5" data-id="element-2587" />} color="emerald" subtitle={`${Math.round(stats.pass / Math.max(stats.total, 1) * 100)}% van totaal`} data-id="element-2586" />
        <StatCard label="Geblokkeerd" value={stats.fail.toLocaleString('nl-NL')} icon={<XCircle className="w-5 h-5" data-id="element-2589" />} color={stats.fail === 0 ? 'emerald' : 'red'} subtitle={`${stats.review} vereisen review`} data-id="element-2588" />
        <StatCard label="Kwaliteitsscore" value={`${stats.avgScore}%`} icon={<TrendingUp className="w-5 h-5" data-id="element-2591" />} color={stats.avgScore >= 80 ? 'emerald' : stats.avgScore >= 60 ? 'amber' : 'red'} subtitle="Gemiddelde over alle vragen" data-id="element-2590" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2592">
        {/* Left Column: Top Blockers & Pipeline Stages */}
        <div className="lg:col-span-2 space-y-6" data-id="element-2593">
          {/* Top Blockers */}
          <Card className="p-5" data-id="element-2594">
            <div className="flex items-center justify-between mb-4" data-id="element-2595">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-2596">
                <AlertTriangle className="w-4 h-4 text-red-500" data-id="element-2597" /> Top Blockers
              </h3>
              {topBlockers.length > 0 && <button onClick={() => onNavigateToQuestion('blockers')} className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1" data-id="element-2598">
                  Bekijk alle <ArrowRight className="w-3 h-3" data-id="element-2599" />
                </button>}
            </div>

            {topBlockers.length === 0 ? <div className="bg-gray-50 rounded-lg p-8 text-center border border-gray-100" data-id="element-2600">
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" data-id="element-2601" />
                <p className="text-sm font-medium text-gray-700" data-id="element-2602">
                  Geen geblokkeerde vragen!
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-2603">
                  Alle vragen komen door de pipeline.
                </p>
              </div> : <div className="space-y-2" data-id="element-2604">
                {topBlockers.map(row => {
              const firstBlocker = row.pipelineIssues.find(i => i.kind === 'blocker');
              const modConfig = MODULE_CONFIG[row.moduleCode] || {
                bgClass: 'bg-gray-500'
              };
              return <div key={row.questionId} className="flex items-start gap-3 p-3 rounded-lg border border-red-100 bg-red-50/50 hover:bg-red-50 transition-colors cursor-pointer" onClick={() => onNavigateToQuestion(row.questionId)} data-id="element-2605">
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${modConfig.bgClass}`} data-id="element-2606" />
                      <div className="min-w-0 flex-1" data-id="element-2607">
                        <div className="flex items-center gap-2 mb-1" data-id="element-2608">
                          <span className="font-mono text-xs font-bold text-gray-900" data-id="element-2609">
                            {row.questionId}
                          </span>
                          <Badge variant="danger" size="sm" data-id="element-2610">
                            FAIL
                          </Badge>
                          <span className="text-[10px] text-gray-500" data-id="element-2611">
                            {row.blockerCount} blocker
                            {row.blockerCount !== 1 ? 's' : ''}
                          </span>
                        </div>
                        {firstBlocker && <p className="text-sm text-red-800 truncate" data-id="element-2612">
                            <span className="font-semibold text-xs uppercase mr-1" data-id="element-2613">
                              [{firstBlocker.domain}]
                            </span>
                            {firstBlocker.message}
                          </p>}
                      </div>
                    </div>;
            })}
              </div>}
          </Card>

          {/* Pipeline Stages */}
          <Card className="p-5" data-id="element-2614">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-2615">
              <Zap className="w-4 h-4 text-amber-500" data-id="element-2616" /> Pipeline Fasen
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2617">
              {PIPELINE_STAGES.map(stage => {
              const count = pipelineIssueCounts?.[stage.domain] ?? 0;
              const status = !pipelineIssueCounts ? 'pending' : count === 0 ? 'ok' : count <= 5 ? 'warning' : 'error';
              const statusConfig = {
                ok: {
                  dot: 'bg-emerald-500',
                  bg: 'bg-emerald-50'
                },
                warning: {
                  dot: 'bg-amber-500',
                  bg: 'bg-amber-50'
                },
                error: {
                  dot: 'bg-red-500',
                  bg: 'bg-red-50'
                },
                pending: {
                  dot: 'bg-gray-300',
                  bg: 'bg-gray-50'
                }
              }[status];
              return <div key={stage.name} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-gray-50/50" data-id="element-2618">
                    <div className="flex items-center gap-3" data-id="element-2619">
                      <div className={`w-2 h-2 rounded-full ${statusConfig.dot}`} data-id="element-2620" />
                      <span className="text-sm font-medium text-gray-700" data-id="element-2621">
                        {stage.label}
                      </span>
                    </div>
                    {status !== 'pending' && <Badge variant={status === 'ok' ? 'success' : status === 'warning' ? 'warning' : 'danger'} size="sm" data-id="element-2622">
                        {count === 0 ? '0 issues' : `${count} issues`}
                      </Badge>}
                  </div>;
            })}
            </div>
          </Card>
        </div>

        {/* Right Column: Module Health */}
        <div className="space-y-6" data-id="element-2623">
          <Card className="p-5" data-id="element-2624">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-2625">
              <FileText className="w-4 h-4 text-blue-500" data-id="element-2626" /> Module Gezondheid
            </h3>
            <div className="space-y-3" data-id="element-2627">
              {moduleHealth.map(mod => {
              const config = MODULE_CONFIG[mod.code];
              const bgClass = config?.bgClass ?? 'bg-gray-400';
              return <div key={mod.code} className="space-y-1.5" data-id="element-2628">
                    <div className="flex items-center justify-between text-xs" data-id="element-2629">
                      <div className="flex items-center gap-1.5 font-medium text-gray-700" data-id="element-2630">
                        <div className={`w-1.5 h-1.5 rounded-full ${bgClass}`} data-id="element-2631" />
                        {mod.code}
                      </div>
                      <div className="flex items-center gap-2" data-id="element-2632">
                        <span className="text-gray-500" data-id="element-2633">{mod.total} vr</span>
                        <span className={`font-bold ${mod.pct === 100 ? 'text-emerald-600' : mod.pct >= 80 ? 'text-amber-600' : 'text-red-600'}`} data-id="element-2634">
                          {mod.pct}%
                        </span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-2635">
                      <div className={`h-full rounded-full ${mod.pct === 100 ? 'bg-emerald-500' : mod.pct >= 80 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                    width: `${mod.pct}%`
                  }} data-id="element-2636" />
                    </div>
                  </div>;
            })}
            </div>
          </Card>
        </div>
      </div>
    </div>;
}